import { React, useMemo, useState, useEffect } from "react";
import PlantillaScreen from "../../helpers/PlantillaScreen";
import { useHistory } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import Swal from "sweetalert2";
import { LoaderScreen } from "../../components/LoaderScreen";
import { BuildingIcon, UsersIcon } from "./OrgIcons";
import AgregarLeadModal from "./AgregarLeadModal";
import EditarLeadModal from "./EditarLeadModal";
import request from "../../../Helpers/API";

export const ConfigurarLeadsMainScreen = () => {
    const history = useHistory();
    const [leads, setLeads] = useState([]);
    const [empleados, setEmpleados] = useState([]);
    const [sucursales, setSucursales] = useState([]);
    const [areas, setAreas] = useState([]);
    const [openModal, setOpenModal] = useState(false);
    const [openModalEditar, setOpenModalEditar] = useState(false);
    const [selectedLead, setSelectedLead] = useState(null);

    const [loader, setLoader] = useState({
        mostrar: false,
        alerta: false,
        error: false,
        mensaje: "",
    });

    const cargarDatos = () => {
        setLoader({ ...loader, mostrar: true });
        request
            .get(`LeadsAgente/obtenerLeads`)
            .then((result) => {
                setLoader({ ...loader, mostrar: false });
                setLeads(result.leads ?? []);
                setEmpleados(result.empleados ?? []);
                setSucursales(result.sucursales ?? []);
                setAreas(result.areas ?? []);
            })
            .catch((error) => {
                setLoader({ ...loader, mostrar: false });
                Swal.fire("LEADS", JSON.stringify(error), "error");
            });
    };

    useEffect(() => {
        cargarDatos();
    }, [openModal, openModalEditar]);

    // Estructura: sucursal (id_sucursal + descripcion) -> área -> [responsables]
    const organigrama = useMemo(() => {
        const porSucursal = {};

        leads.forEach((lead) => {
            if (!porSucursal[lead.sucursal]) {
                porSucursal[lead.sucursal] = { descripcion: lead.descripcion, areas: {} };
            }
            if (!porSucursal[lead.sucursal].areas[lead.area]) {
                porSucursal[lead.sucursal].areas[lead.area] = [];
            }
            porSucursal[lead.sucursal].areas[lead.area].push(lead);
        });

        return Object.entries(porSucursal).map(([idSucursal, datos]) => ({
            idSucursal,
            descripcion: datos.descripcion,
            areas: Object.entries(datos.areas).map(([area, responsables]) => ({
                area,
                responsables: [...responsables].sort((a, b) => a.orden - b.orden),
            })),
        }));
    }, [leads]);

    const handleOpenModal = () => setOpenModal(true);
    const handleCloseModal = () => setOpenModal(false);

    const handleOpenModalEditar = (item) => {
        setSelectedLead(item);
        setOpenModalEditar(true);
    };
    const handleCloseModalEditar = () => {
        setSelectedLead(null);
        setOpenModalEditar(false);
    };

    const onCambiarEstatus = (idLead) => {
        setLoader({ ...loader, mostrar: true });
        request
            .get(`LeadsAgente/CambiaEstatusLead?idlead=${idLead}`)
            .then(() => {
                cargarDatos();
            })
            .catch((error) => {
                setLoader({ ...loader, mostrar: false });
                Swal.fire("LEADS", JSON.stringify(error), "error");
            });
    };

    const onEliminar = (idLead) => {
        Swal.fire({
            title: "¿Eliminar responsable?",
            text: "Esta acción no se puede deshacer.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Sí, eliminar",
            cancelButtonText: "Cancelar",
        }).then((result) => {
            if (!result.isConfirmed) return;
            setLoader({ ...loader, mostrar: true });
            request
                .get(`LeadsAgente/EliminarLead?idlead=${idLead}`)
                .then(() => {
                    Swal.fire("LEADS", "Responsable eliminado correctamente", "success");
                    cargarDatos();
                })
                .catch((error) => {
                    setLoader({ ...loader, mostrar: false });
                    Swal.fire("LEADS", JSON.stringify(error), "error");
                });
        });
    };

    return (
        <PlantillaScreen
            titulo="CONFIGURAR LEADS"
            className="configurar-leads-main-screen"
            className_main={1}
        >
            <div className="configurar-leads-actions">
                <div
                    className="configurar-leads-actions-regresar"
                    onClick={() => history.goBack()}
                >
                    <ArrowBackIcon /> Regresar
                </div>
                <div className="buttons">
                    <button
                        className="configurar-leads-btn-agregar"
                        onClick={handleOpenModal}
                    >
                        <AddCircleOutlineIcon /> Nuevo Responsable
                    </button>
                </div>
            </div>

            <div className="configurar-leads-container">
                <LoaderScreen loader={loader} />

                <div className="orgchart-scroll scroll-4">
                    {organigrama.length === 0 && (
                        <div className="orgchart-empty">Sin responsables configurados.</div>
                    )}

                    <div className="orgchart-container">
                    {organigrama.map((suc) => (
                        <div className="orgchart-card" key={suc.idSucursal}>
                            {/* Nivel 1 — Sucursal */}
                            <div className="orgchart-level orgchart-level--sucursal">
                                <div className="orgchart-node orgchart-node--sucursal">
                                    <div className="orgchart-node__icon"><BuildingIcon /></div>
                                    <div className="orgchart-node__label">{suc.descripcion}</div>
                                </div>
                            </div>

                            <div className="orgchart-connector orgchart-connector--root" />

                            {/* Nivel 2 — Áreas (con su propio scroll horizontal si no caben) */}
                            <div className="orgchart-branches-scroll scroll-4">
                                <div className="orgchart-level orgchart-level--areas">
                                    {suc.areas.map((dep) => (
                                        <div className="orgchart-branch" key={dep.area}>
                                            <div className="orgchart-branch__tallo" />

                                            <div className="orgchart-node orgchart-node--area">
                                                <div className="orgchart-node__icon"><UsersIcon /></div>
                                                <div className="orgchart-node__label">{dep.area}</div>
                                            </div>

                                            <div className="orgchart-connector orgchart-connector--hijos" />

                                            {/* Nivel 3 — Responsables */}
                                            <div className="orgchart-level orgchart-level--responsables">
                                                {dep.responsables.map((resp) => {
                                                    const esSiempre = resp.tipo_usuario === "SI";
                                                    const inactivo = !resp.estatus;

                                                    return (
                                                        <div
                                                            key={resp.id_lead}
                                                            className={
                                                                "responsable-card" +
                                                                (resp.recibe_notificacion ? " responsable-card--activo" : "") +
                                                                (inactivo ? " responsable-card--inactivo" : "")
                                                            }
                                                        >
                                                            <div className="responsable-card__orden">#{resp.orden}</div>

                                                            <div className="responsable-card__info">
                                                                <span className="responsable-card__nombre">{resp.usuario}</span>
                                                                <div className="responsable-card__badges">
                                                                    {esSiempre && (
                                                                        <span className="status-badge status-badge--siempre">
                                                                            Siempre notifica
                                                                        </span>
                                                                    )}
                                                                    <span
                                                                        className={
                                                                            "status-badge " +
                                                                            (inactivo ? "status-badge--inactivo" : "status-badge--activo")
                                                                        }
                                                                        onClick={() => onCambiarEstatus(resp.id_lead)}
                                                                        title="Cambiar estatus"
                                                                    >
                                                                        {inactivo ? "Inactivo" : "Activo"}
                                                                    </span>
                                                                </div>
                                                            </div>

                                                            {resp.recibe_notificacion && (
                                                                <div className="responsable-card__flag" title="Recibe la notificación actualmente">
                                                                    Recibe notificación
                                                                </div>
                                                            )}

                                                            <div className="responsable-card__acciones">
                                                                <button
                                                                    className="responsable-card__btn responsable-card__btn--editar"
                                                                    onClick={() => handleOpenModalEditar(resp)}
                                                                >
                                                                    Editar
                                                                </button>
                                                                <button
                                                                    className="responsable-card__btn responsable-card__btn--eliminar"
                                                                    onClick={() => onEliminar(resp.id_lead)}
                                                                >
                                                                    Eliminar
                                                                </button>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ))}
                    </div>
                </div>

                <AgregarLeadModal
                    open={openModal}
                    onClose={handleCloseModal}
                    empleados={empleados}
                    sucursales={sucursales}
                    areas={areas}
                />
                <EditarLeadModal
                    open={openModalEditar}
                    onClose={handleCloseModalEditar}
                    lead={selectedLead}
                />
            </div>
        </PlantillaScreen>
    );
};
